import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { COOKIE_CONSENT_KEY } from '../components/CookieBanner'

const GA_ID = import.meta.env.VITE_GA_TRACKING_ID
const GTM_ID = import.meta.env.VITE_GTM_ID

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void
    dataLayer: unknown[]
  }
}

const isValidId = (id: string | undefined, placeholder: string) =>
  id && id !== placeholder

const loadGTM = () => {
  if (!isValidId(GTM_ID, 'GTM-XXXXXXX') || document.getElementById('gtm-script')) return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.id = 'gtm-script'
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  document.head.insertBefore(script, document.head.firstChild)
}

const loadGA = () => {
  if (!isValidId(GA_ID, 'GA_MEASUREMENT_ID') || document.getElementById('ga-script')) return

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', GA_ID)

  const script = document.createElement('script')
  script.id = 'ga-script'
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(script)
}

const loadAnalytics = () => {
  loadGTM()
  loadGA()
}

const trackPageView = (path: string) => {
  if (!window.gtag || !isValidId(GA_ID, 'GA_MEASUREMENT_ID')) return
  window.gtag('config', GA_ID, { page_path: path })
}

export const useGoogleAnalytics = () => {
  const location = useLocation()

  useEffect(() => {
    if (localStorage.getItem(COOKIE_CONSENT_KEY) === 'accepted') {
      loadAnalytics()
    }

    const handleConsentUpdate = () => {
      if (localStorage.getItem(COOKIE_CONSENT_KEY) === 'accepted') {
        loadAnalytics()
      }
    }

    window.addEventListener('cookieConsentUpdated', handleConsentUpdate)
    return () => window.removeEventListener('cookieConsentUpdated', handleConsentUpdate)
  }, [])

  useEffect(() => {
    if (localStorage.getItem(COOKIE_CONSENT_KEY) === 'accepted') {
      trackPageView(location.pathname + location.search)
    }
  }, [location])
}
